import React, { useMemo, useState } from "react";
import { TableCellWithOverlappingIcons } from "../../../../data-model/tableCells";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";
import { getIconConfig } from "../../utils/getIconConfig";

export interface Icon {
  icon_id: string;
  hover_text: string;
}

interface OverlappingIconsWithPopoverAndActionsCellProps {
  value: TableCellWithOverlappingIcons;
  onOverlappingIconClick?: (icon_id: string, order_id: string) => void;
  [key: string]: any;
}

export const OverlappingIconsWithPopoverAndActionsCell: React.FC<
  OverlappingIconsWithPopoverAndActionsCellProps
> = ({ value, onOverlappingIconClick }) => {
  const [hoveredIconId, setHoveredIconId] = useState<string | null>(null);

  const validIcons = useMemo(
    () =>
      (value.icons || []).filter(
        (icon): icon is Icon => icon != null && icon.icon_id != null,
      ),
    [value.icons],
  );

  const iconIdsKey = useMemo(
    () =>
      validIcons
        .map((icon) => icon.icon_id)
        .sort()
        .join(","),
    [validIcons],
  );

  const iconRefs = useMemo(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    validIcons.forEach((icon) => {
      refs[icon.icon_id] = React.createRef<HTMLDivElement>();
    });
    return refs;
  }, [iconIdsKey, validIcons]);

  const handleIconClick = (icon_id: string, order_id: string) => {
    if (onOverlappingIconClick) {
      onOverlappingIconClick(icon_id, order_id);
    }
  };

  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex items-center">
        {validIcons.map((icon, index) => {
          const iconRef = iconRefs[icon.icon_id];
          const isHovered = hoveredIconId === icon.icon_id;

          const { backgroundColor, iconComponent } = getIconConfig(
            icon.icon_id,
          );

          return (
            <div
              key={icon.icon_id}
              ref={iconRef}
              className="relative cursor-pointer"
              style={{
                marginLeft: index > 0 ? "-8px" : "0",
                zIndex: validIcons.length + index,
              }}
              onMouseEnter={() => setHoveredIconId(icon.icon_id)}
              onMouseLeave={() => setHoveredIconId(null)}
              onClick={(e) => {
                e.stopPropagation();
                handleIconClick(icon.icon_id, value.order_id);
              }}
            >
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  width: "1.375rem",
                  height: "1.375rem",
                  backgroundColor: backgroundColor,
                }}
              >
                {iconComponent}
              </div>
              {isHovered && iconRef && iconRef.current && (
                <ToolTipUsingPortal
                  title={icon.hover_text}
                  placement="left"
                  showTooltip={isHovered}
                  parentRef={iconRef}
                  children={null}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OverlappingIconsWithPopoverAndActionsCell;
